import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { Button, Select, Spin, Tag } from 'antd';
import Table from '../../../../TableResizable';
import type { ColumnsType } from 'antd/es/table';
import { Add, Delete } from '@mui/icons-material';
import SweetAlert from 'sweetalert2';
import Swal from 'sweetalert2';
import Data from '../../../../../Data/Data.json';
import { Crypter } from '../../../../../Functions/Security/Crypter';
import formatNumber from '../../../../../Functions/Traitement/FormatNumber';
import ShowFile from '../../../../ShowFile';
import InputForm from '../../../../InputT';
import DataTypeTable from '../../../../../Types/DataTypeTable';

interface SelectData {
  value: string;
  label: string;
}

interface Tab2Props {
  form?: any;
  Datagarantie: SelectData[];
  garantiesTableau: DataTypeTable[];
  setGarantiesTableau: React.Dispatch<React.SetStateAction<DataTypeTable[]>>;
}

function Tab2({ form, Datagarantie, garantiesTableau, setGarantiesTableau }: Tab2Props) {
  const [loading] = useState(false);
  const [valeur, setValeur] = useState<string>('');
  const [montant, setMontant] = useState<number>(0);
  const [pourcentage, setPourcentage] = useState<number>(100);

  useEffect(() => {
    if (!garantiesTableau.length) {
      setValeur('');
      setMontant(0);
      setPourcentage(100);
    }
  }, [garantiesTableau]);

  const total = useMemo(
    () => garantiesTableau.reduce((acc, g) => acc + Number(g.valCouvert || 0), 0),
    [garantiesTableau]
  );

  const remove = useCallback((record: DataTypeTable) => {
    SweetAlert.fire({
      title: 'Retirer cette garantie ?',
      icon: 'question',
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      const rows = garantiesTableau.filter((x) => x.codgar !== record.codgar);
      setGarantiesTableau(rows);
      localStorage.setItem(Data.LocalVariable.garantie, JSON.stringify(rows));
    });
  }, [garantiesTableau, setGarantiesTableau]);

  const columns = useMemo<ColumnsType<DataTypeTable>>(() => [
    {
      title: 'Garantie',
      dataIndex: 'CodeGarantie',
      width: 130,
      fixed: 'left',
      render: (x) => <Tag color="blue">{x}</Tag>,
    },
    {
      title: 'Identification',
      dataIndex: 'NUMIDENTI',
      width: 150,
    },
    {
      title: 'Type',
      dataIndex: 'typgar',
      width: 120,
    },
    {
      title: 'Valeur',
      dataIndex: 'VALGARANTI',
      width: 160,
      render: (val) => {
        const num = Number(val);
        return isNaN(num) ? '-' : formatNumber(num) + ' XAF';
      },
    },
    {
      title: 'Couverture',
      dataIndex: 'valCouvert',
      width: 160,
      render: (val) => {
        const num = Number(val);
        return isNaN(num) ? '-' : <span style={{ color: '#1677ff', fontWeight: 700 }}>{formatNumber(num)} XAF</span>;
      },
    },
    {
      title: 'Désignation',
      dataIndex: 'DESIGNATION',
    },
    {
      title: 'Fichier',
      width: 90,
      render: (_, r) => (r.fichier ? <ShowFile file={r.fichier} fileName={r.codgar} /> : '-'),
    },
    {
      title: '',
      width: 80,
      fixed: 'right',
      render: (_, r) => <Button danger icon={<Delete />} onClick={() => remove(r)} />,
    },
  ], [remove]);

  const montantMax = valeur ? Number(valeur.split('|')[1]) : 0;

  const selectGarantie = (value: string) => {
    const dispo = Number(value.split('|')[1]);
    if (dispo <= 0) {
      Swal.fire({ title: 'Montant indisponible', icon: 'warning' });
      return;
    }
    setValeur(value);
    setMontant(dispo);
    setPourcentage(100);
  };

  const add = () => {
    if (!valeur) {
      SweetAlert.fire({ title: 'Sélectionnez une garantie', icon: 'warning' });
      return;
    }

    const code = valeur.split('|')[0];
    const max = Number(valeur.split('|')[1]);

    if (garantiesTableau.find((x) => x.codgar === code)) {
      SweetAlert.fire({ title: 'Garantie déjà ajoutée', icon: 'warning' });
      return;
    }

    if (montant <= 0 || montant > max) {
      SweetAlert.fire({ title: 'Montant invalide', icon: 'warning' });
      return;
    }

    let source: any[] = [];
    try {
      const saved = localStorage.getItem('garanties_client');
      if (saved) source = JSON.parse(Crypter.decrypt(saved));
    } catch {}

    const detail = source.find((x) => x.codgar === code) || {};
    const row = {
      ...detail,
      key: code,
      CodeGarantie: code,   // ← clé unifiée
      valCouvert: montant,
    };

    const list = [...garantiesTableau, row];
    setGarantiesTableau(list);
    localStorage.setItem(Data.LocalVariable.garantie, JSON.stringify(list));

    setValeur('');
    setMontant(0);
    setPourcentage(100);
  };

  return (
    <Spin spinning={loading}>
      <Box sx={{ width: '85%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ p: 2, borderRadius: 3, border: '1px solid #d9d9d9', background: '#fafafa' }}>
          <Box sx={{ mb: 2, fontWeight: 700 }}>Ajouter une garantie</Box>
          <Box display="grid" gap={2} sx={{ gridTemplateColumns: { xs: '1fr', lg: '2fr 120px 200px auto auto' } }}>
            <Select
              value={valeur || undefined}
              options={Datagarantie}
              showSearch
              placeholder="Sélectionner"
              onChange={selectGarantie}
            />
            <InputForm
              type="number"
              value={pourcentage}
              label="%"
              disabled={!valeur}
              onChange={(e: any) => {
                const p = Number(e.target.value);
                setPourcentage(p);
                setMontant(Math.round((p * montantMax) / 100));
              }}
            />
            <InputForm
              type="number"
              label="Montant"
              value={montant}
              disabled={!valeur}
              onChange={(e: any) => setMontant(Number(e.target.value))}
            />
            <Button type="primary" style={{ color: 'white' }} icon={<Add />} onClick={add}>
              Ajouter
            </Button>
          </Box>
        </Box>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, background: '#001529', color: '#fff' }}>
            <b>Garanties ({garantiesTableau.length})</b>
            <b>Total : {formatNumber(total)} XAF</b>
          </Box>
          <Table
            rowKey="CodeGarantie"
            sticky
            size="middle"
            columns={columns}
            dataSource={garantiesTableau}
            scroll={{ x: 1800, y: 520 }}
            pagination={{ pageSize: 8, showSizeChanger: true }}
          />
        </Box>
      </Box>
    </Spin>
  );
}

export default Tab2;